'use es6';
/*
 * State
 */

/*
{
  byAsset {
    default: {
      isPreviewOpen: false,
      previewOptions: {
        previewDevice: PreviewDevices.DESKTOP,
        previewBlogId: null,
        previewBlogPostView: null,
        previewLayoutId: -1,
        previewDomain: 'default',
      },
    },
    12345: {
      isPreviewOpen: true,
      previewOptions: {
        previewDevice: PreviewDevices.DESKTOP,
        previewBlogId: null,
        previewBlogPostView: null,
        previewLayoutId: 78162347,
        previewDomain: 'default',

        // New preview options properties, not available in DMUI
        previewHeight: 1234,
        previewWidth: 1234,
        previewDevicePreset: [RESPONSIVE, MOBILE, TABLET],
        previewOrientation: [PORTRAIT, LANDSCAPE],
        previewScale: 05/FIT,
      },
    },
  },
}
*/

import { PREVIEW_OPTIONS_FETCH, PREVIEW_OPTIONS_DELETE, PREVIEW_OPTIONS_UPDATE_PREVIEW_CONFIGURATION, PREVIEW_OPTIONS_TOGGLE_PREVIEW, PREVIEW_OPTIONS_FETCH_BUILDS, PREVIEW_OPTIONS_CHECK_TEMPLATE_BY_BUILD } from 'DesignData/actions/ActionTypes';
import { PREVIEW_DEVICES, ZOOM_PRESETS } from 'DesignData/enums/PreviewOptions';
import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { omit, defaults } from 'underscore';
import PreviewDevices from 'DesignData/enums/PreviewDevices';
import { isNullOrUndefined } from 'DesignData/lib/validation';
import { getTimestampForAction } from 'DesignData/lib/actions';
import { isExpired, createUpdatedTimestampForMissing } from 'DesignData/lib/localStorage';
import { EN } from 'DesignData/constants/LanguageCodes';
const DEFAULT_PREVIEW_DATA = {
  previewHeight: 0,
  previewWidth: 0,
  previewDevicePreset: PREVIEW_DEVICES.RESPONSIVE,
  previewScale: ZOOM_PRESETS.FIT,
  previewLanguage: EN
};
/*
 * Nested reducers under byAsset
 */

const byAssetReducers = combineReducers({
  /*
   * Preview Configuration
   */
  previewConfiguration(state = SeamlessImmutable(Object.assign({
    previewDevice: PreviewDevices.DESKTOP,
    previewBlogId: null,
    previewBlogPostView: null,
    previewLayoutId: -1,
    previewDomain: 'default',
    previewModuleData: {}
  }, DEFAULT_PREVIEW_DATA)), action) {
    switch (action.type) {
      case PREVIEW_OPTIONS_UPDATE_PREVIEW_CONFIGURATION:
        {
          const {
            previewConfiguration
          } = action;
          return state.merge(previewConfiguration);
        }

      default:
        return state;
    }
  },

  /*
   * isPreviewOpen
   */
  isPreviewOpen(state = false, action) {
    switch (action.type) {
      case PREVIEW_OPTIONS_TOGGLE_PREVIEW:
        {
          return !state;
        }

      default:
        return state;
    }
  },

  /**
   * updated
   */
  updated(state = null, action) {
    switch (action.type) {
      case PREVIEW_OPTIONS_UPDATE_PREVIEW_CONFIGURATION:
      case PREVIEW_OPTIONS_TOGGLE_PREVIEW:
        return getTimestampForAction(action);

      default:
        return state;
    }
  }

});
/*
 * byAsset
 */

function byAsset(state = SeamlessImmutable({
  default: byAssetReducers(undefined, {})
}), action) {
  switch (action.type) {
    case PREVIEW_OPTIONS_FETCH:
      {
        const {
          previewOptions
        } = action;

        if (!(previewOptions && previewOptions.byAsset)) {
          return state;
        } // Do not use persisted default data - keep that defined here.


        const byAssetData = previewOptions.byAsset.default ? omit(previewOptions.byAsset, 'default') : previewOptions.byAsset;
        const updatedAssetsData = Object.keys(byAssetData).reduce((acc, assetKey) => {
          const {
            isPreviewOpen,
            previewConfiguration,
            updated
          } = byAssetData[assetKey]; // Timestamps were added later, this populates old timestamps
          // and subtracts time to expire them sooner in case they are very old.

          const timestamp = updated || createUpdatedTimestampForMissing();

          if (isExpired(byAssetData[assetKey])) {
            return acc;
          }

          acc[assetKey] = {
            isPreviewOpen,
            previewConfiguration: defaults(omit(previewConfiguration, isNullOrUndefined), DEFAULT_PREVIEW_DATA),
            updated: timestamp
          };
          return acc;
        }, {});
        return state.merge(updatedAssetsData);
      }

    case PREVIEW_OPTIONS_UPDATE_PREVIEW_CONFIGURATION:
      {
        const {
          assetId
        } = action;
        return state.set(assetId, byAssetReducers(state[assetId], action));
      }

    case PREVIEW_OPTIONS_DELETE:
      {
        const {
          assetId
        } = action;
        return state.without(assetId);
      }

    case PREVIEW_OPTIONS_TOGGLE_PREVIEW:
      {
        const {
          assetId
        } = action;
        return state.set(assetId, byAssetReducers(state[assetId], action));
      }

    default:
      {
        return state;
      }
  }
}
/*
 * projectOptions
 */


function projectOptions(state = SeamlessImmutable({
  builds: [],
  error: null,
  templateBuilds: {}
}), action) {
  switch (action.type) {
    case PREVIEW_OPTIONS_FETCH_BUILDS:
      {
        const {
          buildList = [],
          error
        } = action;
        const builds = buildList.map(build => ({
          id: build.buildId,
          status: build.status
        }));
        return SeamlessImmutable.merge(state, {
          builds,
          error
        });
      }

    case PREVIEW_OPTIONS_CHECK_TEMPLATE_BY_BUILD:
      {
        const {
          deployId,
          isTemplateInBuild
        } = action;
        return SeamlessImmutable.merge(state, {
          templateBuilds: Object.assign({}, state.templateBuilds, {
            [deployId]: isTemplateInBuild
          })
        });
      }

    default:
      {
        return state;
      }
  }
}
/*
 * Export
 */


export default combineReducers({
  byAsset,
  projectOptions
});